import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import { useAuth } from '../contexts/AuthContext';
import { motion } from 'framer-motion';
import { Check, ArrowRight, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fadeUp = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } };
const stagger = { visible: { transition: { staggerChildren: 0.08 } } };

const tiers = [
  {
    name: "Karma Assist",
    price: 399,
    color: "#7D9B6A",
    tagline: "Your personal guide to doing karma",
    features: [
      "Guided karma activities every month",
      "Step-by-step instructions",
      "Nearby location suggestions",
      "WhatsApp support for guidance",
      "Timely reminders",
      "Personal karma tracking",
    ],
    tier_id: "karma_assist"
  },
  {
    name: "Karma Circle",
    price: 1999,
    color: "#D4A54A",
    tagline: "Experience karma together",
    features: [
      "Access to curated group karma events",
      "Small, like-minded groups (5-15 people)",
      "Feeding people (Bhandara)",
      "Old age home seva",
      "Animal care activities",
      "All logistics managed",
      "Community-driven experience",
    ],
    popular: true,
    tier_id: "karma_circle"
  },
];

export default function SubscriptionPage() {
  const { user, refreshUser } = useAuth();
  const [processing, setProcessing] = useState('');
  const [mySub, setMySub] = useState(null);

  useEffect(() => { fetchSubscription(); }, []);

  const fetchSubscription = async () => {
    try {
      const { data } = await axios.get(`${API}/subscriptions/my`, { withCredentials: true });
      setMySub(data.subscription);
    } catch (err) { console.error(err); }
  };

  const handleSubscribe = async (tier) => {
    setProcessing(tier.tier_id);
    try {
      const { data } = await axios.post(`${API}/subscriptions/create-order`, {
        tier: tier.tier_id, amount: tier.price
      }, { withCredentials: true });

      if (data.mock) {
        await axios.post(`${API}/subscriptions/verify`, { order_id: data.order_id }, { withCredentials: true });
        toast.success(`Subscribed to ${tier.name}! (Demo Mode)`);
        await refreshUser(); fetchSubscription();
      } else {
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.onload = () => {
          const options = {
            key: data.key_id, amount: data.amount, currency: data.currency, order_id: data.order_id,
            name: 'KarmaCircle', description: `${tier.name} Subscription`,
            handler: async (response) => {
              try {
                await axios.post(`${API}/subscriptions/verify`, {
                  order_id: data.order_id, payment_id: response.razorpay_payment_id, signature: response.razorpay_signature
                }, { withCredentials: true });
                toast.success(`Subscribed to ${tier.name}!`);
                await refreshUser(); fetchSubscription();
              } catch { toast.error('Payment verification failed'); }
            },
            prefill: { email: user?.email, name: user?.name },
            theme: { color: '#D4A54A' }
          };
          const rzp = new window.Razorpay(options);
          rzp.open();
        };
        document.body.appendChild(script);
      }
    } catch { toast.error('Failed to create order'); } finally { setProcessing(''); }
  };

  const isActiveTier = (tierId) => mySub?.tier === tierId && mySub?.status === 'active';

  return (
    <div className="min-h-screen bg-[#110A06]">
      <Header />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12" data-testid="subscription-page">
        <motion.div initial="hidden" animate="visible" variants={stagger} className="text-center mb-12">
          <motion.p variants={fadeUp} className="font-sans text-xs uppercase tracking-[0.2em] font-bold text-[#D4A54A] mb-1">Subscription</motion.p>
          <motion.h1 variants={fadeUp} className="font-serif text-3xl sm:text-4xl tracking-tight text-[#F5E6C8]">Choose Your Path</motion.h1>
          <motion.p variants={fadeUp} className="font-sans text-sm text-[#A89070] mt-2">Select a plan that resonates with your karma journey</motion.p>
        </motion.div>

        {mySub && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-[#D4A54A]/10 border border-[#D4A54A]/20 rounded-2xl p-6 mb-8 text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Sparkles className="w-5 h-5 text-[#D4A54A]" />
              <span className="font-sans text-sm font-semibold text-[#D4A54A]">Active Subscription</span>
            </div>
            <p className="font-serif text-2xl font-semibold text-[#F5E6C8]">{mySub.tier.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</p>
            <p className="font-sans text-xs text-[#A89070] mt-1">Since {mySub.activated_at ? new Date(mySub.activated_at).toLocaleDateString('en-IN') : 'recently'}</p>
          </motion.div>
        )}

        <motion.div initial="hidden" animate="visible" variants={stagger} className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {tiers.map((tier) => (
            <motion.div
              key={tier.tier_id} variants={fadeUp}
              data-testid={`sub-card-${tier.tier_id}`}
              className={`bg-[#1A1008] rounded-3xl border ${tier.popular ? 'border-[#D4A54A] gold-glow' : 'border-[#3D2D1D]/50'} p-8 relative hover:border-[#D4A54A]/50 transition-all duration-300`}
            >
              {tier.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#D4A54A] text-[#110A06] text-xs font-sans font-bold uppercase tracking-wider px-4 py-1 rounded-full">
                  Most Popular
                </div>
              )}
              <div className="mb-6">
                <div className="w-3 h-3 rounded-full mb-4" style={{ backgroundColor: tier.color }} />
                <h3 className="font-serif text-2xl font-semibold text-[#F5E6C8] mb-1">{tier.name}</h3>
                <p className="font-sans text-sm text-[#A89070] italic">{tier.tagline}</p>
              </div>
              <div className="flex items-baseline gap-1 mb-6">
                <span className="font-serif text-4xl font-semibold text-[#D4A54A]">{'\u20B9'}{tier.price.toLocaleString('en-IN')}</span>
                <span className="font-sans text-sm text-[#A89070]">/ month</span>
              </div>
              <ul className="space-y-3 mb-8">
                {tier.features.map((f, j) => (
                  <li key={j} className="flex items-start gap-3">
                    <Check className="w-4 h-4 text-[#D4A54A] mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                    <span className="font-sans text-sm text-[#A89070]">{f}</span>
                  </li>
                ))}
              </ul>
              {isActiveTier(tier.tier_id) ? (
                <div data-testid={`active-badge-${tier.tier_id}`} className="w-full text-center bg-[#7D9B6A]/10 text-[#7D9B6A] rounded-full px-8 py-3 font-sans font-medium">
                  Current Plan
                </div>
              ) : (
                <button
                  onClick={() => handleSubscribe(tier)}
                  disabled={processing === tier.tier_id}
                  data-testid={`subscribe-btn-${tier.tier_id}`}
                  className={`w-full rounded-full px-8 py-3 font-sans font-bold transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg disabled:opacity-50 flex items-center justify-center gap-2 ${
                    tier.popular
                      ? 'bg-[#D4A54A] hover:bg-[#C49540] text-[#110A06] hover:shadow-[#D4A54A]/20'
                      : 'bg-[#7D9B6A] hover:bg-[#6B8A58] text-white'
                  }`}
                >
                  {processing === tier.tier_id ? 'Processing...' : 'Subscribe Now'} <ArrowRight className="w-4 h-4" />
                </button>
              )}
            </motion.div>
          ))}
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="mt-8 bg-gradient-to-br from-[#1A1008] to-[#23180E] rounded-3xl border border-[#3D2D1D]/50 p-8 md:p-12 text-center">
          <h3 className="font-serif text-2xl sm:text-3xl font-semibold text-[#F5E6C8] mb-3">Looking for Personalized Karma?</h3>
          <p className="font-sans text-sm text-[#A89070] max-w-lg mx-auto mb-6">A deeply personal, private, and meaningful experience aligned with your life goals. 1-on-1 consultation, customized planning, and premium arrangements.</p>
          <a href="/#contact" data-testid="personalized-contact-btn" className="inline-flex items-center gap-2 bg-[#D4A54A] hover:bg-[#C49540] text-[#110A06] rounded-full px-8 py-3 font-sans font-bold transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-[#D4A54A]/20">
            Contact Us <ArrowRight className="w-4 h-4" />
          </a>
        </motion.div>
      </div>
    </div>
  );
}
